import { produce } from "immer";
import { omit, sortBy } from "lodash";
import { AddOn, Experience, Form, PurchaseRuleLink, Seller } from "types";
import { initialValues, PaymentPreference } from "../ExperienceVirtualMeeting";
import { saveAddons, saveDemographics } from "./DuplicateCatalogItemsHelper";
import { getDuplicateFormPayload, getPaths, omitId, refetchDuplicatedExperience, saveData } from "./DuplicateHelper";
import { saveFees, savePricingRulesAndLinks } from "./DuplicatePricingHelper";
import { saveSchedules, SchedulesMap } from "./DuplicateSchedulesHelper";
import { AllowedDuplicateItems, ExperienceCheckInPreferences, PreferenceLabels } from "@/models/DuplicateExperience";
import { sanitizeExperiencePayload } from "@/models/Experience";

export const getDuplicateExperiencePayload = (
    experience: Experience,
    selectedItems: Record<string, { selected: boolean }>,
) => {
    const newExperience = {
        ...omit(experience, [
            "id",
            "photo",
            "medias",
            "attachments",
            ...Object.values(ExperienceCheckInPreferences),
            ...Object.values(AllowedDuplicateItems),
        ]),
    };

    const virtualMeetingDefaultValues = {
        ...initialValues,
        enabled: !!experience.virtualMeeting?.enabled,
        payment: PaymentPreference.Full,
    };

    const payload = produce(newExperience, (draft) => {
        draft.name = `Copy of ${experience.name}`;

        if (experience.virtualMeeting?.enabled) {
            draft.virtualMeeting = virtualMeetingDefaultValues;
        }

        if (!experience.duration) {
            draft.duration = 1;
        }

        if (selectedItems.checkInPreference?.selected) {
            draft.firstCheckinExpirationOffset = experience.firstCheckinExpirationOffset;
            draft.purchaseExpirationOffset = experience.purchaseExpirationOffset;
        }
    });

    return payload;
};

export const getAutoAcceptPayload = (seller: Seller, experience: Experience) => ({
    autoAccept: {
        ...seller.autoAccept,
        experiences: [...seller.autoAccept.experiences, experience.id],
    },
});

interface handleInvalidateProps {
    experience: Experience;
    seller: Seller;
    invalidate: (collection: string, id?: string) => void;
}

export const handleInvalidate = ({ experience, seller, invalidate }: handleInvalidateProps) => {
    invalidate("products");
    invalidate("experiences", experience.id);
    invalidate("forms");
    invalidate("purchaseRules");
    invalidate("purchaseRuleLinks");
    invalidate("sellers", seller.id);
};

interface getProcessedPreferencesProps {
    availableToCopy: Array<{ label: string; key: string }>;
    selectedItems: Record<string, { selected: boolean }>;
    experience: Experience;
}

export const getProcessedPreferences = ({
    availableToCopy,
    selectedItems,
    experience,
}: getProcessedPreferencesProps) => {
    const processedItems = availableToCopy.map((item) => ({
        ...item,
        selected: selectedItems[item?.key]?.selected,
    }));

    return {
        experienceId: experience.id,
        items: sortBy(processedItems, ["selected"]),
    };
};

interface DuplicateExperienceProps {
    originalExperience: Experience;
    selectedItems: Record<string, { selected: boolean }>;
    seller: Seller;
    pricingPurchaseRuleLinks: PurchaseRuleLink[];
    form: Form | undefined;
    addOns: AddOn[];
    schedulePurchaseRuleLinks: PurchaseRuleLink[];
    feePurchaseRuleLinks: PurchaseRuleLink[];
}

export const duplicateExperience = async ({
    originalExperience,
    selectedItems,
    seller,
    pricingPurchaseRuleLinks,
    form,
    addOns,
    schedulePurchaseRuleLinks,
    feePurchaseRuleLinks,
}: DuplicateExperienceProps) => {
    const response: any = await saveData({
        payload: sanitizeExperiencePayload(getDuplicateExperiencePayload(originalExperience, selectedItems)),
        path: "/experiences",
        item: PreferenceLabels.EXPERIENCE,
    });

    const { data: experience }: { data: Experience } = response;

    const paths = getPaths(experience, seller);

    let schedulesMap: SchedulesMap = [];

    if (originalExperience?.catalog?.items?.length) {
        await saveDemographics({
            catalogItems: originalExperience.catalog.items,
            experience,
            path: paths.catalogItems,
        });
    }

    if (selectedItems.forms?.selected && form) {
        await saveData({
            payload: getDuplicateFormPayload(form, experience.id),
            path: paths.forms,
            item: PreferenceLabels.FORMS,
        });
    }

    if (selectedItems.addOns?.selected) {
        await saveAddons({ addOns, experience, path: paths.catalogItems, seller });
    }

    if (selectedItems.schedules?.selected) {
        const schedules = await saveSchedules({
            schedules: originalExperience.schedules,
            schedulePurchaseRuleLinks,
            experience,
            seller,
            path: paths.schedules,
        });

        schedulesMap = schedules;
    }

    if (pricingPurchaseRuleLinks?.length) {
        await savePricingRulesAndLinks({
            pricingPurchaseRuleLink: pricingPurchaseRuleLinks[0],
            experience,
            originalExperience,
            seller,
            schedulesMap,
        });
    }

    if (selectedItems.terms?.selected) {
        await saveData({
            payload: omitId(originalExperience.terms),
            path: paths.terms,
            item: PreferenceLabels.TERMS,
        });
    }

    if (selectedItems.travelerPreference?.selected) {
        await saveData({
            payload: originalExperience.travelerPreference,
            path: paths.travelerPreference,
            item: PreferenceLabels.TRAVELER,
        });
    }

    if (selectedItems.guidePreference?.selected) {
        await saveData({
            payload: originalExperience.guidePreference,
            path: paths.guide,
            item: PreferenceLabels.GUIDES,
        });
    }

    if (selectedItems.reviewPreference?.selected) {
        await saveData({
            payload: omitId(originalExperience.reviewPreference),
            path: paths.review,
            item: PreferenceLabels.REVIEWS,
        });
    }

    if (selectedItems.waitlist?.selected) {
        await saveData({
            payload: omitId(originalExperience.waitlist),
            path: paths.waitlist,
            item: PreferenceLabels.WAITLIST,
        });
    }

    if (selectedItems.checkInPreference?.selected && originalExperience.checkInPreference) {
        const validations =
            originalExperience.checkInPreference?.validations.map((validation) => omitId(validation)) ?? [];

        await saveData({
            payload: { ...originalExperience.checkInPreference, validations },
            path: paths.checkIn,
            item: PreferenceLabels.CHECK_IN,
        });
    }

    if (selectedItems.qrCodePreference?.selected) {
        await saveData({
            payload: originalExperience.qrCodePreference,
            path: paths.qrCode,
            item: PreferenceLabels.QR_CODE,
        });
    }

    if (selectedItems.waiverPreference?.selected) {
        await saveData({
            payload: originalExperience.waiverPreference,
            path: paths.waivers,
            item: PreferenceLabels.WAIVERS,
        });
    }

    if (selectedItems.fees?.selected) {
        await saveFees({ feePurchaseRuleLinks, experience });
    }

    if (selectedItems.autoAcceptSettings?.selected) {
        await saveData({
            payload: getAutoAcceptPayload(seller, experience),
            path: paths.autoAccept,
            item: PreferenceLabels.AUTO_ACCEPT,
        });
    }

    if (selectedItems.virtualMeeting?.selected) {
        await saveData({
            payload: originalExperience.virtualMeeting,
            path: paths.virtualMeeting,
            item: PreferenceLabels.VIRTUAL_MEETING,
        });
    }

    const experienceData = await refetchDuplicatedExperience(experience.id, seller.id);

    await saveData({
        payload: experienceData,
        path: paths.experiences,
        item: PreferenceLabels.EXPERIENCE,
    });

    return experience;
};
